// =================================================================
// RecentSessionsSubmenu.js — S44 Phase 3a
// Sous-menu "Dernières discussions" dans la sidebar (sous Projets)
// Liste les 10 sessions les plus récentes toutes projets confondus
// =================================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MoreHorizontal, MessageCircle, ChevronRight } from 'lucide-react';

const REFRESH_INTERVAL_MS = 60_000;
const LS_COLLAPSED = 'itriia_recent_sessions_collapsed_v1';

// Formatage timestamp relatif concis (fr-FR, sans dépendance)
const relativeTime = (iso) => {
  if (!iso) return '';
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'à l\'instant';
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `il y a ${d} j`;
  const w = Math.floor(d / 7);
  if (w < 5) return `il y a ${w} sem.`;
  return new Date(iso).toLocaleDateString('fr-FR');
};

const RecentSessionsSubmenu = ({ onOpen, limit = 10 }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(LS_COLLAPSED) === '1'; } catch { return false; }
  });
  const menuRef = useRef(null);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(LS_COLLAPSED, next ? '1' : '0'); } catch { /* silent */ }
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      const resp = await axios.get('/api/projects/recent-sessions', { params: { limit } });
      setSessions(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      // Silencieux : ne pas polluer le chat log en cas d'erreur transitoire
      console.warn('recent-sessions load failed:', e?.response?.status || e.message);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load, limit]);

  // Fermer menu contextuel au clic extérieur
  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId !== null) {
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }
  }, [openMenuId]);

  const handleRename = async (s) => {
    const newTitle = window.prompt('Nouveau titre de la discussion :', s.title || '');
    if (!newTitle || newTitle.trim() === (s.title || '').trim()) return;
    try {
      await axios.put(`/api/projects/${s.project_id}/sessions/${s.id}/rename`, { title: newTitle.trim() });
      load();
    } catch { /* silent */ }
    setOpenMenuId(null);
  };

  const handleArchive = async (s) => {
    try {
      await axios.put(`/api/projects/${s.project_id}/sessions/${s.id}/archive`);
      load();
    } catch { /* silent */ }
    setOpenMenuId(null);
  };

  const handleDownload = async (s) => {
    // V1 : ouvre directement la session, export batch arrivera en phase 3b
    onOpen?.(s);
    setOpenMenuId(null);
  };

  return (
    <div className="recent-sessions-submenu">
      <style>{`
        .recent-sessions-submenu {
          margin: 4px 8px 8px 16px;
          padding: 6px 0 4px;
          border-left: 2px solid var(--card-border);
          background: var(--sub-items-bg);
          border-radius: 8px;
        }
        .recent-sessions-header {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.7px;
          text-transform: uppercase;
          color: var(--sidebar-section-label, #a5b4fc);
          padding: 8px 10px 6px;
          width: 100%;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
          border-radius: 6px;
          transition: background 0.15s ease;
        }
        .recent-sessions-header:hover {
          background: var(--sidebar-active-bg);
        }
        .rs-header-chevron {
          flex-shrink: 0;
          opacity: 0.75;
          transition: transform 0.15s ease;
        }
        .rs-header-chevron.open { transform: rotate(90deg); }
        .recent-session-item {
          position: relative;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 10px;
          margin: 1px 6px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12.5px;
          color: var(--sidebar-text);
          transition: background 0.15s ease, color 0.15s ease;
        }
        .recent-session-item:hover {
          background: var(--sidebar-active-bg);
          color: var(--sidebar-active);
        }
        .rs-icon {
          flex-shrink: 0;
          opacity: 0.75;
        }
        .rs-main {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 1px;
        }
        .rs-title {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-weight: 500;
        }
        .rs-meta {
          font-size: 10.5px;
          color: var(--text-muted, #6a6a85);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .rs-actions-btn {
          background: none;
          border: none;
          color: var(--text-muted, #6a6a85);
          padding: 2px;
          border-radius: 4px;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.15s ease, background 0.15s ease;
          flex-shrink: 0;
        }
        .recent-session-item:hover .rs-actions-btn {
          opacity: 1;
        }
        .rs-actions-btn:hover {
          background: var(--sidebar-active-bg);
          color: var(--sidebar-active);
        }
        .rs-menu {
          position: absolute;
          right: 4px;
          top: 100%;
          margin-top: 2px;
          background: var(--card-bg);
          border: 1px solid var(--card-border);
          border-radius: 6px;
          box-shadow: 0 6px 16px rgba(0,0,0,0.18);
          z-index: 50;
          min-width: 160px;
          padding: 4px;
        }
        .rs-menu-item {
          width: 100%;
          text-align: left;
          padding: 6px 10px;
          font-size: 12px;
          background: none;
          border: none;
          border-radius: 4px;
          color: var(--text-primary, var(--sidebar-text));
          cursor: pointer;
        }
        .rs-menu-item:hover {
          background: var(--sidebar-active-bg);
          color: var(--sidebar-active);
        }
        .rs-empty {
          padding: 8px 12px;
          font-size: 11.5px;
          color: var(--text-muted, #6a6a85);
          font-style: italic;
        }
      `}</style>

      <button
        type="button"
        className="recent-sessions-header"
        onClick={toggleCollapsed}
        title={collapsed ? 'Afficher les dernières discussions' : 'Masquer'}
      >
        <ChevronRight size={11} className={`rs-header-chevron ${!collapsed ? 'open' : ''}`} />
        <span>Dernières discussions</span>
      </button>

      {!collapsed && loading && sessions.length === 0 && (
        <div className="rs-empty">Chargement…</div>
      )}
      {!collapsed && !loading && sessions.length === 0 && (
        <div className="rs-empty">Commencez une discussion dans un projet</div>
      )}

      {!collapsed && sessions.map((s) => (
        <div
          key={s.id}
          className="recent-session-item"
          onClick={() => onOpen?.(s)}
          title={`${s.project_name} · ${s.title || 'Sans titre'}`}
          ref={openMenuId === s.id ? menuRef : null}
        >
          <MessageCircle size={12} className="rs-icon" />
          <div className="rs-main">
            <div className="rs-title">{s.title || 'Nouvelle conversation'}</div>
            <div className="rs-meta">
              {(s.project_icon ? s.project_icon + ' ' : '') + s.project_name}
              {s.messages_count > 0 ? ` · ${s.messages_count} msg` : ''}
              {' · '}{relativeTime(s.updated_at)}
            </div>
          </div>
          <button
            className="rs-actions-btn"
            onClick={(e) => {
              e.stopPropagation();
              setOpenMenuId(openMenuId === s.id ? null : s.id);
            }}
            title="Actions"
          >
            <MoreHorizontal size={14} />
          </button>
          {openMenuId === s.id && (
            <div className="rs-menu" onClick={(e) => e.stopPropagation()}>
              <button className="rs-menu-item" onClick={() => { onOpen?.(s); setOpenMenuId(null); }}>Ouvrir</button>
              <button className="rs-menu-item" onClick={() => handleRename(s)}>Renommer</button>
              <button className="rs-menu-item" onClick={() => handleArchive(s)}>Archiver</button>
              <button className="rs-menu-item" onClick={() => handleDownload(s)}>Télécharger</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default RecentSessionsSubmenu;
